import React from 'react';
import {View} from 'react-native';
import {Keyboard} from 'react-native';
import {get, noop} from 'lodash';

import Avatar from '/component/base/Avatar';
import Icon from '/component/base/Icon';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {SearchAndFilter} from '/constant/TestIDs';
import {EVENT_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {generateCallback} from '/util/component.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import TestProperties from '/util/testing/testProperties';

import {styles} from './SearchResultItem.style';
interface GenerationInfo {
  isFrontline?: boolean;
  level?: number;
}
interface SearchItem {
  id?: number | string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}
interface SearchResultItemProps {
  avatarUrl: string;
  isCoach: boolean;
  fullName: string;
  generation: GenerationInfo | null;
  onPress: () => void;
  addToRecentSearches: (payload: {item: SearchItem}) => void;
  id: number | string | null;
  item: SearchItem;
  deleteFromRecentSearches: (payload: {item: SearchItem}) => void;
  isRecentSearch: boolean;
  currentScreen: string;
  lastPaidRank: string;
}

export const SearchResultItem: React.FC<SearchResultItemProps> = ({
  avatarUrl = '',
  isCoach = false,
  fullName = '',
  generation = null,
  onPress = noop,
  addToRecentSearches = noop,
  id = null,
  item = {},
  deleteFromRecentSearches = noop,
  isRecentSearch = false,
  currentScreen = '',
  lastPaidRank = '',
}) => {
  const {ts} = useTranslation('screen.search.results');
  const itemStyle = isRecentSearch ? styles.itemRecent : styles.item;

  const isFrontline = get(generation, 'isFrontline');
  const genLevel = get(generation, 'level');

  const genLevelText = isFrontline
    ? ts('frontline', 'Frontline')
    : `${ts('generation', 'Generation')} ${genLevel}`;

  const role = isCoach ? ts('coach', 'Coach') : ts('client', 'Client');

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.userClick,
  });

  const onPressHandler = () => {
    onPress();
    addToRecentSearches({item});
    sendEvent({
      userType: isCoach ? USER_TYPES.coach : USER_TYPES.client,
      userId: item.id,
      userFullName: item.firstName + ' ' + item.lastName,
      screenName: currentScreen,
      lastPaidRank: lastPaidRank,
    });
    Keyboard.dismiss();
  };

  return (
    <View style={itemStyle}>
      <ListItem
        onPress={onPressHandler}
        label={
          <View
            style={styles.base}
            testID={SearchAndFilter.searchResultsItem}
            accessible={false}>
            <Avatar isCoach={isCoach} avatarUrl={avatarUrl} avatarKey={id} />
            <View
              style={styles.textWrapper}
              accessible={false}
              accessibilityRole="button">
              <Text
                variant="button"
                testID={SearchAndFilter.searchResultsFullName}>
                {fullName}
              </Text>
              <Text
                emphasis="low"
                testID={
                  SearchAndFilter.searchResultsRoleAndGeneration
                }>{`${role}   /   ${genLevelText}`}</Text>
            </View>
          </View>
        }
        suffix={
          !isRecentSearch && (
            <Icon
              name="chevron-right"
              style={styles.arrowIcon}
              accessible={false}
            />
          )
        }
      />
      {isRecentSearch && (
        <TouchableOpacity
          onPress={generateCallback({
            func: deleteFromRecentSearches,
            params: {item},
          })}
          {...TestProperties(null, true)}>
          <Icon name="close" style={styles.arrowIcon} />
        </TouchableOpacity>
      )}
    </View>
  );
};
